import * as z from 'zod';

export const GetMenuOrderingSettingsRequest = z.object({
  menuOrderingSettingsId: z
    .string()
    .describe('ID of the menu ordering settings entity to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetMenuOrderingSettingsResponse = z.object({
  _id: z
    .string()
    .describe('Menu ordering settings object ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number. Increments by 1 each time the menu ordering settings object is updated. To prevent conflicting changes, the existing revision must be specified when updating a menu ordering settings object.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe(
      'Date and time the menu ordering settings object was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date and time the menu ordering settings object was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  operationId: z
    .string()
    .describe(
      'ID of the restaurant operation the menu belongs to. (See the Restaurants Operations API for more information.)'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  menuId: z
    .string()
    .describe('ID of the menu these settings apply to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  onlineOrderingEnabled: z
    .boolean()
    .describe('Whether online ordering is enabled for the menu.')
    .optional()
    .nullable(),
  availability: z
    .intersection(
      z.object({
        type: z
          .enum([
            'UNSPECIFIED_AVAILABILITY_OPTION',
            'ALWAYS_AVAILABLE',
            'WEEKLY_SCHEDULE',
            'TIMESTAMP_RANGES',
          ])
          .describe('Availability type.')
          .optional(),
        timeZone: z
          .string()
          .describe(
            'Time zone in [Time Zone Database](https://www.iana.org/time-zones) format.'
          )
          .max(50)
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          weeklyScheduleOptions: z.never().optional(),
          timestampRangesOptions: z.never().optional(),
        }),
        z.object({
          timestampRangesOptions: z.never().optional(),
          weeklyScheduleOptions: z
            .object({
              availableTimes: z
                .array(
                  z.object({
                    dayOfWeek: z
                      .enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'])
                      .describe('The day of week this availability relates to.')
                      .optional(),
                    timeRanges: z
                      .array(
                        z.object({
                          startTime: z
                            .object({
                              hours: z
                                .number()
                                .int()
                                .describe(
                                  'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                              minutes: z
                                .number()
                                .int()
                                .describe(
                                  'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                            })
                            .describe(
                              'The start time in time of day representation.'
                            )
                            .optional(),
                          endTime: z
                            .object({
                              hours: z
                                .number()
                                .int()
                                .describe(
                                  'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                              minutes: z
                                .number()
                                .int()
                                .describe(
                                  'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                            })
                            .describe(
                              'The end time in time of day representation.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                  })
                )
                .max(7)
                .optional(),
            })
            .describe('Settings for availability on a weekly schedule.'),
        }),
        z.object({
          weeklyScheduleOptions: z.never().optional(),
          timestampRangesOptions: z
            .object({
              ranges: z
                .array(
                  z.object({
                    startTime: z
                      .date()
                      .describe(
                        'The start time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
                      )
                      .optional()
                      .nullable(),
                    endTime: z
                      .date()
                      .describe(
                        'The end time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe('Settings for availability within a time range.'),
        }),
      ])
    )
    .describe('Menu availability settings.')
    .optional(),
  businessLocationId: z
    .string()
    .describe(
      'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the menu.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of menu ordering settings.'
    )
    .optional(),
});
export const UpsertMenuOrderingSettingsByMenuIdRequest = z.object({
  menuId: z
    .string()
    .describe('ID of the menu these settings apply to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  menuOrderingSettings: z
    .object({
      _id: z
        .string()
        .describe('Menu ordering settings object ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number. Increments by 1 each time the menu ordering settings object is updated. To prevent conflicting changes, the existing revision must be specified when updating a menu ordering settings object.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the menu ordering settings object was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the menu ordering settings object was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      operationId: z
        .string()
        .describe(
          'ID of the restaurant operation the menu belongs to. (See the Restaurants Operations API for more information.)'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      menuId: z
        .string()
        .describe('ID of the menu these settings apply to.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      onlineOrderingEnabled: z
        .boolean()
        .describe('Whether online ordering is enabled for the menu.')
        .optional()
        .nullable(),
      availability: z
        .intersection(
          z.object({
            type: z
              .enum([
                'UNSPECIFIED_AVAILABILITY_OPTION',
                'ALWAYS_AVAILABLE',
                'WEEKLY_SCHEDULE',
                'TIMESTAMP_RANGES',
              ])
              .optional(),
            timeZone: z
              .string()
              .describe(
                'Time zone in [Time Zone Database](https://www.iana.org/time-zones) format.'
              )
              .max(50)
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              weeklyScheduleOptions: z.never().optional(),
              timestampRangesOptions: z.never().optional(),
            }),
            z.object({
              timestampRangesOptions: z.never().optional(),
              weeklyScheduleOptions: z
                .object({
                  availableTimes: z
                    .array(
                      z.object({
                        dayOfWeek: z
                          .enum([
                            'MON',
                            'TUE',
                            'WED',
                            'THU',
                            'FRI',
                            'SAT',
                            'SUN',
                          ])
                          .optional(),
                        timeRanges: z
                          .array(
                            z.object({
                              startTime: z
                                .object({
                                  hours: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                  minutes: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'The start time in time of day representation.'
                                )
                                .optional(),
                              endTime: z
                                .object({
                                  hours: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                  minutes: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'The end time in time of day representation.'
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                    )
                    .max(7)
                    .optional(),
                })
                .describe('Settings for availability on a weekly schedule.'),
            }),
            z.object({
              weeklyScheduleOptions: z.never().optional(),
              timestampRangesOptions: z
                .object({
                  ranges: z
                    .array(
                      z.object({
                        startTime: z
                          .date()
                          .describe(
                            'The start time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
                          )
                          .optional()
                          .nullable(),
                        endTime: z
                          .date()
                          .describe(
                            'The end time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
                          )
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(50)
                    .optional(),
                })
                .describe('Settings for availability within a time range.'),
            }),
          ])
        )
        .describe('Menu availability settings.')
        .optional(),
      businessLocationId: z
        .string()
        .describe(
          'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the menu.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of menu ordering settings.'
        )
        .optional(),
    })
    .describe(
      'Details of the menu ordering settings entity to be created or updated.'
    ),
});
export const UpsertMenuOrderingSettingsByMenuIdResponse = z.object({
  menuOrderingSettings: z
    .object({
      _id: z
        .string()
        .describe('Menu ordering settings object ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number. Increments by 1 each time the menu ordering settings object is updated. To prevent conflicting changes, the existing revision must be specified when updating a menu ordering settings object.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the menu ordering settings object was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the menu ordering settings object was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      operationId: z
        .string()
        .describe(
          'ID of the restaurant operation the menu belongs to. (See the Restaurants Operations API for more information.)'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      menuId: z
        .string()
        .describe('ID of the menu these settings apply to.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      onlineOrderingEnabled: z
        .boolean()
        .describe('Whether online ordering is enabled for the menu.')
        .optional()
        .nullable(),
      availability: z
        .intersection(
          z.object({
            type: z
              .enum([
                'UNSPECIFIED_AVAILABILITY_OPTION',
                'ALWAYS_AVAILABLE',
                'WEEKLY_SCHEDULE',
                'TIMESTAMP_RANGES',
              ])
              .describe('Availability type.')
              .optional(),
            timeZone: z
              .string()
              .describe(
                'Time zone in [Time Zone Database](https://www.iana.org/time-zones) format.'
              )
              .max(50)
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              weeklyScheduleOptions: z.never().optional(),
              timestampRangesOptions: z.never().optional(),
            }),
            z.object({
              timestampRangesOptions: z.never().optional(),
              weeklyScheduleOptions: z
                .object({
                  availableTimes: z
                    .array(
                      z.object({
                        dayOfWeek: z
                          .enum([
                            'MON',
                            'TUE',
                            'WED',
                            'THU',
                            'FRI',
                            'SAT',
                            'SUN',
                          ])
                          .describe(
                            'The day of week this availability relates to.'
                          )
                          .optional(),
                        timeRanges: z
                          .array(
                            z.object({
                              startTime: z
                                .object({
                                  hours: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                  minutes: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'The start time in time of day representation.'
                                )
                                .optional(),
                              endTime: z
                                .object({
                                  hours: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                  minutes: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'The end time in time of day representation.'
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                    )
                    .max(7)
                    .optional(),
                })
                .describe('Settings for availability on a weekly schedule.'),
            }),
            z.object({
              weeklyScheduleOptions: z.never().optional(),
              timestampRangesOptions: z
                .object({
                  ranges: z
                    .array(
                      z.object({
                        startTime: z
                          .date()
                          .describe(
                            'The start time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
                          )
                          .optional()
                          .nullable(),
                        endTime: z
                          .date()
                          .describe(
                            'The end time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
                          )
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(50)
                    .optional(),
                })
                .describe('Settings for availability within a time range.'),
            }),
          ])
        )
        .describe('Menu availability settings.')
        .optional(),
      businessLocationId: z
        .string()
        .describe(
          'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the menu.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of menu ordering settings.'
        )
        .optional(),
    })
    .describe('The created or updated menu ordering settings entity.')
    .optional(),
});
export const UpdateMenuOrderingSettingsRequest = z.object({
  _id: z
    .string()
    .describe('Menu ordering settings object ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  menuOrderingSettings: z
    .object({
      _id: z
        .string()
        .describe('Menu ordering settings object ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number. Increments by 1 each time the menu ordering settings object is updated. To prevent conflicting changes, the existing revision must be specified when updating a menu ordering settings object.'
        ),
      _createdDate: z
        .date()
        .describe(
          'Date and time the menu ordering settings object was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the menu ordering settings object was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      operationId: z
        .string()
        .describe(
          'ID of the restaurant operation the menu belongs to. (See the Restaurants Operations API for more information.)'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      menuId: z
        .string()
        .describe('ID of the menu these settings apply to.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      onlineOrderingEnabled: z
        .boolean()
        .describe('Whether online ordering is enabled for the menu.')
        .optional()
        .nullable(),
      availability: z
        .intersection(
          z.object({
            type: z
              .enum([
                'UNSPECIFIED_AVAILABILITY_OPTION',
                'ALWAYS_AVAILABLE',
                'WEEKLY_SCHEDULE',
                'TIMESTAMP_RANGES',
              ])
              .optional(),
            timeZone: z
              .string()
              .describe(
                'Time zone in [Time Zone Database](https://www.iana.org/time-zones) format.'
              )
              .max(50)
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              weeklyScheduleOptions: z.never().optional(),
              timestampRangesOptions: z.never().optional(),
            }),
            z.object({
              timestampRangesOptions: z.never().optional(),
              weeklyScheduleOptions: z
                .object({
                  availableTimes: z
                    .array(
                      z.object({
                        dayOfWeek: z
                          .enum([
                            'MON',
                            'TUE',
                            'WED',
                            'THU',
                            'FRI',
                            'SAT',
                            'SUN',
                          ])
                          .optional(),
                        timeRanges: z
                          .array(
                            z.object({
                              startTime: z
                                .object({
                                  hours: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                  minutes: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'The start time in time of day representation.'
                                )
                                .optional(),
                              endTime: z
                                .object({
                                  hours: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                  minutes: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'The end time in time of day representation.'
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                    )
                    .max(7)
                    .optional(),
                })
                .describe('Settings for availability on a weekly schedule.'),
            }),
            z.object({
              weeklyScheduleOptions: z.never().optional(),
              timestampRangesOptions: z
                .object({
                  ranges: z
                    .array(
                      z.object({
                        startTime: z
                          .date()
                          .describe(
                            'The start time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
                          )
                          .optional()
                          .nullable(),
                        endTime: z
                          .date()
                          .describe(
                            'The end time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
                          )
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(50)
                    .optional(),
                })
                .describe('Settings for availability within a time range.'),
            }),
          ])
        )
        .describe('Menu availability settings.')
        .optional(),
      businessLocationId: z
        .string()
        .describe(
          'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the menu.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of menu ordering settings.'
        )
        .optional(),
    })
    .describe('Details of the menu ordering settings entity to update.'),
});
export const UpdateMenuOrderingSettingsResponse = z.object({
  _id: z
    .string()
    .describe('Menu ordering settings object ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number. Increments by 1 each time the menu ordering settings object is updated. To prevent conflicting changes, the existing revision must be specified when updating a menu ordering settings object.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe(
      'Date and time the menu ordering settings object was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date and time the menu ordering settings object was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  operationId: z
    .string()
    .describe(
      'ID of the restaurant operation the menu belongs to. (See the Restaurants Operations API for more information.)'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  menuId: z
    .string()
    .describe('ID of the menu these settings apply to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  onlineOrderingEnabled: z
    .boolean()
    .describe('Whether online ordering is enabled for the menu.')
    .optional()
    .nullable(),
  availability: z
    .intersection(
      z.object({
        type: z
          .enum([
            'UNSPECIFIED_AVAILABILITY_OPTION',
            'ALWAYS_AVAILABLE',
            'WEEKLY_SCHEDULE',
            'TIMESTAMP_RANGES',
          ])
          .describe('Availability type.')
          .optional(),
        timeZone: z
          .string()
          .describe(
            'Time zone in [Time Zone Database](https://www.iana.org/time-zones) format.'
          )
          .max(50)
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          weeklyScheduleOptions: z.never().optional(),
          timestampRangesOptions: z.never().optional(),
        }),
        z.object({
          timestampRangesOptions: z.never().optional(),
          weeklyScheduleOptions: z
            .object({
              availableTimes: z
                .array(
                  z.object({
                    dayOfWeek: z
                      .enum(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'])
                      .describe('The day of week this availability relates to.')
                      .optional(),
                    timeRanges: z
                      .array(
                        z.object({
                          startTime: z
                            .object({
                              hours: z
                                .number()
                                .int()
                                .describe(
                                  'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                              minutes: z
                                .number()
                                .int()
                                .describe(
                                  'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                            })
                            .describe(
                              'The start time in time of day representation.'
                            )
                            .optional(),
                          endTime: z
                            .object({
                              hours: z
                                .number()
                                .int()
                                .describe(
                                  'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                              minutes: z
                                .number()
                                .int()
                                .describe(
                                  'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                )
                                .optional(),
                            })
                            .describe(
                              'The end time in time of day representation.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                  })
                )
                .max(7)
                .optional(),
            })
            .describe('Settings for availability on a weekly schedule.'),
        }),
        z.object({
          weeklyScheduleOptions: z.never().optional(),
          timestampRangesOptions: z
            .object({
              ranges: z
                .array(
                  z.object({
                    startTime: z
                      .date()
                      .describe(
                        'The start time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
                      )
                      .optional()
                      .nullable(),
                    endTime: z
                      .date()
                      .describe(
                        'The end time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .max(50)
                .optional(),
            })
            .describe('Settings for availability within a time range.'),
        }),
      ])
    )
    .describe('Menu availability settings.')
    .optional(),
  businessLocationId: z
    .string()
    .describe(
      'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the menu.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of menu ordering settings.'
    )
    .optional(),
});
export const QueryMenuOrderingSettingsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          operationId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          menuId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          onlineOrderingEnabled: z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'availability.type': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          businessLocationId: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_id',
                '_createdDate',
                '_updatedDate',
                'operationId',
                'menuId',
                'onlineOrderingEnabled',
                'availability.type',
                'businessLocationId',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('WQL expression.'),
});
export const QueryMenuOrderingSettingsResponse = z.object({
  menuOrderingSettings: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Menu ordering settings object ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number. Increments by 1 each time the menu ordering settings object is updated. To prevent conflicting changes, the existing revision must be specified when updating a menu ordering settings object.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the menu ordering settings object was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the menu ordering settings object was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
          )
          .optional()
          .nullable(),
        operationId: z
          .string()
          .describe(
            'ID of the restaurant operation the menu belongs to. (See the Restaurants Operations API for more information.)'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        menuId: z
          .string()
          .describe('ID of the menu these settings apply to.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        onlineOrderingEnabled: z
          .boolean()
          .describe('Whether online ordering is enabled for the menu.')
          .optional()
          .nullable(),
        availability: z
          .intersection(
            z.object({
              type: z
                .enum([
                  'UNSPECIFIED_AVAILABILITY_OPTION',
                  'ALWAYS_AVAILABLE',
                  'WEEKLY_SCHEDULE',
                  'TIMESTAMP_RANGES',
                ])
                .describe('Availability type.')
                .optional(),
              timeZone: z
                .string()
                .describe(
                  'Time zone in [Time Zone Database](https://www.iana.org/time-zones) format.'
                )
                .max(50)
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                weeklyScheduleOptions: z.never().optional(),
                timestampRangesOptions: z.never().optional(),
              }),
              z.object({
                timestampRangesOptions: z.never().optional(),
                weeklyScheduleOptions: z
                  .object({
                    availableTimes: z
                      .array(
                        z.object({
                          dayOfWeek: z
                            .enum([
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                              'SUN',
                            ])
                            .describe(
                              'The day of week this availability relates to.'
                            )
                            .optional(),
                          timeRanges: z
                            .array(
                              z.object({
                                startTime: z
                                  .object({
                                    hours: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                      )
                                      .optional(),
                                    minutes: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'The start time in time of day representation.'
                                  )
                                  .optional(),
                                endTime: z
                                  .object({
                                    hours: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                      )
                                      .optional(),
                                    minutes: z
                                      .number()
                                      .int()
                                      .describe(
                                        'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'The end time in time of day representation.'
                                  )
                                  .optional(),
                              })
                            )
                            .optional(),
                        })
                      )
                      .max(7)
                      .optional(),
                  })
                  .describe('Settings for availability on a weekly schedule.'),
              }),
              z.object({
                weeklyScheduleOptions: z.never().optional(),
                timestampRangesOptions: z
                  .object({
                    ranges: z
                      .array(
                        z.object({
                          startTime: z
                            .date()
                            .describe(
                              'The start time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
                            )
                            .optional()
                            .nullable(),
                          endTime: z
                            .date()
                            .describe(
                              'The end time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
                            )
                            .optional()
                            .nullable(),
                        })
                      )
                      .max(50)
                      .optional(),
                  })
                  .describe('Settings for availability within a time range.'),
              }),
            ])
          )
          .describe('Menu availability settings.')
          .optional(),
        businessLocationId: z
          .string()
          .describe(
            'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the menu.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of menu ordering settings.'
          )
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata')
    .optional(),
});
export const BulkUpdateMenuOrderingSettingsRequest = z.object({
  menusOrderingSettings: z
    .array(
      z.object({
        menuOrderingSettings: z
          .object({
            _id: z
              .string()
              .describe('Menu ordering settings object ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number. Increments by 1 each time the menu ordering settings object is updated. To prevent conflicting changes, the existing revision must be specified when updating a menu ordering settings object.'
              ),
            _createdDate: z
              .date()
              .describe(
                'Date and time the menu ordering settings object was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date and time the menu ordering settings object was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            operationId: z
              .string()
              .describe(
                'ID of the restaurant operation the menu belongs to. (See the Restaurants Operations API for more information.)'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            menuId: z
              .string()
              .describe('ID of the menu these settings apply to.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            onlineOrderingEnabled: z
              .boolean()
              .describe('Whether online ordering is enabled for the menu.')
              .optional()
              .nullable(),
            availability: z
              .intersection(
                z.object({
                  type: z
                    .enum([
                      'UNSPECIFIED_AVAILABILITY_OPTION',
                      'ALWAYS_AVAILABLE',
                      'WEEKLY_SCHEDULE',
                      'TIMESTAMP_RANGES',
                    ])
                    .optional(),
                  timeZone: z
                    .string()
                    .describe(
                      'Time zone in [Time Zone Database](https://www.iana.org/time-zones) format.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    weeklyScheduleOptions: z.never().optional(),
                    timestampRangesOptions: z.never().optional(),
                  }),
                  z.object({
                    timestampRangesOptions: z.never().optional(),
                    weeklyScheduleOptions: z
                      .object({
                        availableTimes: z
                          .array(
                            z.object({
                              dayOfWeek: z
                                .enum([
                                  'MON',
                                  'TUE',
                                  'WED',
                                  'THU',
                                  'FRI',
                                  'SAT',
                                  'SUN',
                                ])
                                .optional(),
                              timeRanges: z
                                .array(
                                  z.object({
                                    startTime: z
                                      .object({
                                        hours: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                          )
                                          .optional(),
                                        minutes: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        'The start time in time of day representation.'
                                      )
                                      .optional(),
                                    endTime: z
                                      .object({
                                        hours: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                          )
                                          .optional(),
                                        minutes: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        'The end time in time of day representation.'
                                      )
                                      .optional(),
                                  })
                                )
                                .optional(),
                            })
                          )
                          .max(7)
                          .optional(),
                      })
                      .describe(
                        'Settings for availability on a weekly schedule.'
                      ),
                  }),
                  z.object({
                    weeklyScheduleOptions: z.never().optional(),
                    timestampRangesOptions: z
                      .object({
                        ranges: z
                          .array(
                            z.object({
                              startTime: z
                                .date()
                                .describe(
                                  'The start time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
                                )
                                .optional()
                                .nullable(),
                              endTime: z
                                .date()
                                .describe(
                                  'The end time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
                                )
                                .optional()
                                .nullable(),
                            })
                          )
                          .max(50)
                          .optional(),
                      })
                      .describe(
                        'Settings for availability within a time range.'
                      ),
                  }),
                ])
              )
              .describe('Menu availability settings.')
              .optional(),
            businessLocationId: z
              .string()
              .describe(
                'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the menu.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields.')
              .optional(),
            tags: z
              .object({
                privateTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                  )
                  .optional(),
                tags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                  )
                  .optional(),
              })
              .describe(
                'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of menu ordering settings.'
              )
              .optional(),
          })
          .describe('Menu ordering settings entities to update.')
          .optional(),
        mask: z.array(z.string()).optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe('Whether to receive the entity in the response.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateMenuOrderingSettingsResponse = z.object({
  results: z
    .array(
      z.object({
        menuOrderingSettingsMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for menu update.')
          .optional(),
        menuOrderingSettings: z
          .object({
            _id: z
              .string()
              .describe('Menu ordering settings object ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number. Increments by 1 each time the menu ordering settings object is updated. To prevent conflicting changes, the existing revision must be specified when updating a menu ordering settings object.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the menu ordering settings object was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date and time the menu ordering settings object was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            operationId: z
              .string()
              .describe(
                'ID of the restaurant operation the menu belongs to. (See the Restaurants Operations API for more information.)'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            menuId: z
              .string()
              .describe('ID of the menu these settings apply to.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            onlineOrderingEnabled: z
              .boolean()
              .describe('Whether online ordering is enabled for the menu.')
              .optional()
              .nullable(),
            availability: z
              .intersection(
                z.object({
                  type: z
                    .enum([
                      'UNSPECIFIED_AVAILABILITY_OPTION',
                      'ALWAYS_AVAILABLE',
                      'WEEKLY_SCHEDULE',
                      'TIMESTAMP_RANGES',
                    ])
                    .describe('Availability type.')
                    .optional(),
                  timeZone: z
                    .string()
                    .describe(
                      'Time zone in [Time Zone Database](https://www.iana.org/time-zones) format.'
                    )
                    .max(50)
                    .optional()
                    .nullable(),
                }),
                z.xor([
                  z.object({
                    weeklyScheduleOptions: z.never().optional(),
                    timestampRangesOptions: z.never().optional(),
                  }),
                  z.object({
                    timestampRangesOptions: z.never().optional(),
                    weeklyScheduleOptions: z
                      .object({
                        availableTimes: z
                          .array(
                            z.object({
                              dayOfWeek: z
                                .enum([
                                  'MON',
                                  'TUE',
                                  'WED',
                                  'THU',
                                  'FRI',
                                  'SAT',
                                  'SUN',
                                ])
                                .describe(
                                  'The day of week this availability relates to.'
                                )
                                .optional(),
                              timeRanges: z
                                .array(
                                  z.object({
                                    startTime: z
                                      .object({
                                        hours: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                          )
                                          .optional(),
                                        minutes: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        'The start time in time of day representation.'
                                      )
                                      .optional(),
                                    endTime: z
                                      .object({
                                        hours: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                          )
                                          .optional(),
                                        minutes: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        'The end time in time of day representation.'
                                      )
                                      .optional(),
                                  })
                                )
                                .optional(),
                            })
                          )
                          .max(7)
                          .optional(),
                      })
                      .describe(
                        'Settings for availability on a weekly schedule.'
                      ),
                  }),
                  z.object({
                    weeklyScheduleOptions: z.never().optional(),
                    timestampRangesOptions: z
                      .object({
                        ranges: z
                          .array(
                            z.object({
                              startTime: z
                                .date()
                                .describe(
                                  'The start time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
                                )
                                .optional()
                                .nullable(),
                              endTime: z
                                .date()
                                .describe(
                                  'The end time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
                                )
                                .optional()
                                .nullable(),
                            })
                          )
                          .max(50)
                          .optional(),
                      })
                      .describe(
                        'Settings for availability within a time range.'
                      ),
                  }),
                ])
              )
              .describe('Menu availability settings.')
              .optional(),
            businessLocationId: z
              .string()
              .describe(
                'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the menu.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields.')
              .optional(),
            tags: z
              .object({
                privateTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                  )
                  .optional(),
                tags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                  )
                  .optional(),
              })
              .describe(
                'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of menu ordering settings.'
              )
              .optional(),
          })
          .describe(
            'Updated menu ordering settings. Only returned if `returnEntity` is set to `true`.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the API call.')
    .optional(),
});
export const ListMenusAvailabilityStatusRequest = z.object({
  timeSlot: z
    .object({
      startTime: z
        .date()
        .describe(
          'The start time of the time slot in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
        ),
      endTime: z
        .date()
        .describe(
          'The end time of the time slot in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
        ),
    })
    .describe('The time slot for which to check the availability of menus.'),
  options: z
    .object({
      operationId: z
        .string()
        .describe(
          'The ID of the restaurant operation whose menus will be checked. (See the Restaurants Operations API for more information.)'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      cursorPaging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Maximum number of items to return in the results.')
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Cursor paging')
        .optional(),
    })
    .optional(),
});
export const ListMenusAvailabilityStatusResponse = z.object({
  menusAvailabilityStatus: z
    .array(
      z.object({
        menuId: z
          .string()
          .describe('Menu ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        availabilityStatus: z
          .enum(['UNKNOWN_AVAILABILITY_STATUS', 'AVAILABLE', 'UNAVAILABLE'])
          .describe("The menu's availability status.")
          .optional(),
      })
    )
    .max(100)
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('The metadata of the paginated results.')
    .optional(),
});
export const UpdateExtendedFieldsRequest = z.object({
  _id: z.string().describe('ID of the entity to update.'),
  namespace: z
    .string()
    .describe(
      'Identifier for the app whose extended fields are being updated.'
    ),
  options: z.object({
    namespaceData: z
      .record(z.string(), z.any())
      .describe(
        'Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured.'
      ),
  }),
});
export const UpdateExtendedFieldsResponse = z.object({
  menuOrderingSettings: z
    .object({
      _id: z
        .string()
        .describe('Menu ordering settings object ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number. Increments by 1 each time the menu ordering settings object is updated. To prevent conflicting changes, the existing revision must be specified when updating a menu ordering settings object.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the menu ordering settings object was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the menu ordering settings object was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      operationId: z
        .string()
        .describe(
          'ID of the restaurant operation the menu belongs to. (See the Restaurants Operations API for more information.)'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      menuId: z
        .string()
        .describe('ID of the menu these settings apply to.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      onlineOrderingEnabled: z
        .boolean()
        .describe('Whether online ordering is enabled for the menu.')
        .optional()
        .nullable(),
      availability: z
        .intersection(
          z.object({
            type: z
              .enum([
                'UNSPECIFIED_AVAILABILITY_OPTION',
                'ALWAYS_AVAILABLE',
                'WEEKLY_SCHEDULE',
                'TIMESTAMP_RANGES',
              ])
              .describe('Availability type.')
              .optional(),
            timeZone: z
              .string()
              .describe(
                'Time zone in [Time Zone Database](https://www.iana.org/time-zones) format.'
              )
              .max(50)
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              weeklyScheduleOptions: z.never().optional(),
              timestampRangesOptions: z.never().optional(),
            }),
            z.object({
              timestampRangesOptions: z.never().optional(),
              weeklyScheduleOptions: z
                .object({
                  availableTimes: z
                    .array(
                      z.object({
                        dayOfWeek: z
                          .enum([
                            'MON',
                            'TUE',
                            'WED',
                            'THU',
                            'FRI',
                            'SAT',
                            'SUN',
                          ])
                          .describe(
                            'The day of week this availability relates to.'
                          )
                          .optional(),
                        timeRanges: z
                          .array(
                            z.object({
                              startTime: z
                                .object({
                                  hours: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                  minutes: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'The start time in time of day representation.'
                                )
                                .optional(),
                              endTime: z
                                .object({
                                  hours: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Hours. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                  minutes: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Minutes. <br />\nMin: `0`. <br />\nMax: `23`.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'The end time in time of day representation.'
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                    )
                    .max(7)
                    .optional(),
                })
                .describe('Settings for availability on a weekly schedule.'),
            }),
            z.object({
              weeklyScheduleOptions: z.never().optional(),
              timestampRangesOptions: z
                .object({
                  ranges: z
                    .array(
                      z.object({
                        startTime: z
                          .date()
                          .describe(
                            'The start time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
                          )
                          .optional()
                          .nullable(),
                        endTime: z
                          .date()
                          .describe(
                            'The end time of the availability in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
                          )
                          .optional()
                          .nullable(),
                      })
                    )
                    .max(50)
                    .optional(),
                })
                .describe('Settings for availability within a time range.'),
            }),
          ])
        )
        .describe('Menu availability settings.')
        .optional(),
      businessLocationId: z
        .string()
        .describe(
          'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the menu.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of menu ordering settings.'
        )
        .optional(),
    })
    .describe('The updated menu ordering settings entity.')
    .optional(),
});
export const BulkUpdateMenuOrderingSettingsTagsRequest = z.object({
  menuOrderingSettingsIds: z.array(z.string()).max(100),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to assign to the menu ordering settings entities.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to unassign from the menu ordering settings entities.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateMenuOrderingSettingsTagsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for the updated menu ordering settings entity.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the bulk update.')
    .optional(),
});
export const BulkUpdateMenuOrderingSettingsTagsByFilterRequest = z.object({
  filter: z
    .record(z.string(), z.any())
    .describe(
      'Filter that determines which menu ordering settings entities to update tags for.'
    ),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to assign to the menu ordering settings entities.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to unassign from the menu ordering settings entities.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateMenuOrderingSettingsTagsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      "Job ID. Pass this ID to Get Async Job ([SDK](https://dev.wix.com/docs/sdk/backend-modules/async-jobs/get-async-job) | [REST](https://dev.wix.com/docs/rest/business-management/async-job/get-async-job)) to track the job's status."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
